// Helpers de autenticação reutilizados nas páginas da aplicação.
// Espelha a forma como o CadastroEmpresa expõe identidade do usuário, nível
// de acesso e helpers de cache em sessionStorage. Mantido em /frontend/js
// para que páginas mais profundas consigam importar sem ciclos.

import { jwtDecode } from "jwt-decode";
import { carregarDadosEmpresaPorId } from "../services/empresaService.js";
import { garantirPaginaLimpaAoCarregar } from "../utils/dom.js";

const PAGINA_BLOQUEIO = "/404";

function valorBooleanoAtivo(valor) {
  const normalizado = String(valor || "")
    .trim()
    .toLowerCase();
  return normalizado === "1" || normalizado === "true" || normalizado === "on";
}

function decodificarPayloadJwt(token) {
  try {
    return jwtDecode(String(token || ""));
  } catch {
    return null;
  }
}

/**
 * Variante "safe" usada por outras partes da aplicação (ex.: utils/session.js
 * da página de Acompanhamento) que precisam do payload JWT sem acoplar
 * diretamente ao `jwt-decode`.
 */
export function decodificarPayloadJwtSafe(token) {
  return decodificarPayloadJwt(token);
}

export function getToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("token");
}

export function getNivelAcesso() {
  // 1. Prioridade: JWT (não pode ser manipulado no client).
  const payload = decodificarPayloadJwt(getToken()) || {};
  const nivelToken = Number(
    payload?.tipo_acesso ??
      payload?.nivel_acesso ??
      payload?.role_id ??
      payload?.role ??
      payload?.acesso_autorizacao ??
      0,
  );

  let usuario = {};
  try {
    usuario = JSON.parse(
      window.localStorage.getItem("user") ||
      window.localStorage.getItem("usuario") ||
      "{}"
    );
  } catch {
    usuario = {};
  }

  // Estudante (TCE / TCA Empregador) tem um nível próprio no Acompanhamento (10):
  // lista apenas as autorizações vinculadas ao próprio CPF, sem misturar
  // com cadastros de outras empresas.
  if (
    payload?.tipo === "estudante" ||
    usuario?.tipo === "estudante" ||
    Number(payload?.acesso_autorizacao ?? 0) === 1 ||
    Number(usuario?.acesso_autorizacao ?? 0) === 1 ||
    Number(payload?.tipo_acesso ?? 0) === 1 ||
    Number(usuario?.tipo_acesso ?? 0) === 1
  ) {
    return 10;
  }

  if (Number.isFinite(nivelToken) && nivelToken > 0) return nivelToken;

  const nivelUsuario = Number(
    usuario?.tipo_acesso ??
      usuario?.nivel_acesso ??
      usuario?.role_id ??
      usuario?.acesso_autorizacao ??
      0,
  );

  if (Number.isFinite(nivelUsuario) && nivelUsuario > 0) return nivelUsuario;

  if (payload?.tipo === "usuario_ciee" || usuario?.tipo === "usuario_ciee") {
    return 3;
  }

  return 0;
}

export function parseTokenPayload() {
  return decodificarPayloadJwt(getToken()) || {};
}

/**
 * Resolve o `nivel` (acesso_autorizacao) do usuário a partir de várias
 * fontes. Usado para que páginas como TCA/TCE/TcaEmpregador consigam
 * distinguir Empresa (0), Estudante (1), SEOPE/SECEX/INFOR (>=2)
 * mesmo quando o JWT não traz o claim `acesso_autorizacao`.
 *
 * Ordem de tentativa:
 *  1. `user.acesso_autorizacao` (já calculado no login via Inicial)
 *  2. Claims alternativos do JWT: `tipo_acesso`, `nivel_acesso`,
 *     `role_id`, `role`
 *  3. Fallback: 0 (empresa)
 *
 * Reforço importante: quando `tipo === "usuario_ciee"`, garantimos
 * nível >= 2 — se nenhum claim trouxer nível, assumimos 3 (perfil
 * de aprovador padrão) para que botões Aprovar/Recusar não sumam.
 */
export function getNivelAcessoFromUser(user) {
  if (!user) return 0;

  let nivel = Number(user.acesso_autorizacao ?? 0);
  if (Number.isFinite(nivel) && nivel >= 1) return nivel;

  // Fallback: claims alternativos do JWT já decodificado em `user`
  // (o login do Inicial faz `userData = { ...decoded, ... }`).
  const candidatos = [
    user.tipo_acesso,
    user.nivel_acesso,
    user.role_id,
    Number.isFinite(Number(user.role)) ? Number(user.role) : undefined,
  ];
  for (const c of candidatos) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) nivel = n;
  }

  // Se o usuário explicitamente for estudante ou tiver nível 1, preserva nível 1
  if (user.tipo === "estudante" || nivel === 1) {
    return 1;
  }

  // Para perfis CIEE/SECEX/SEOPE/INFOR, garantimos no mínimo o
  // nível de aprovador (3) se NADA veio do JWT (nivel === 0). Sem isso, qualquer
  // operador de Validações sem o claim explícito vira empresa (0)
  // e os botões Aprovar/Recusar desaparecem do TCA/TCE/TcaEmpregador.
  if (user.tipo === "usuario_ciee" && (!nivel || nivel === 0)) {
    nivel = 3;
  }

  return nivel;
}

/**
 * Versão que recebe diretamente o payload JWT (ou um user-like object)
 * e retorna apenas o número final do nível. Útil em componentes que
 * já têm o `decoded` em mãos.
 */
export function getNivelAcessoFromPayload(payload) {
  if (!payload) return 0;

  let nivel = Number(payload.acesso_autorizacao ?? 0);
  if (Number.isFinite(nivel) && nivel >= 1) return nivel;

  const candidatos = [
    payload.tipo_acesso,
    payload.nivel_acesso,
    payload.role_id,
    Number.isFinite(Number(payload.role)) ? Number(payload.role) : undefined,
  ];
  for (const c of candidatos) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) nivel = n;
  }

  if (payload.tipo === "estudante" || nivel === 1) {
    return 1;
  }

  if (payload.tipo === "usuario_ciee" && (!nivel || nivel === 0)) {
    nivel = 3;
  }

  return nivel;
}

export function isLogado() {
  return !!getToken();
}

export function obterDocumentoUsuarioLogado() {
  if (typeof window === "undefined") return "";

  let usuario = {};
  try {
    usuario = JSON.parse(
      window.localStorage.getItem("user") ||
      window.localStorage.getItem("usuario") ||
      "{}"
    );
  } catch {
    usuario = {};
  }

  const payload = decodificarPayloadJwt(getToken()) || {};

  const candidatos = [
    window.localStorage.getItem("cpf_estudante"),
    window.localStorage.getItem("estudante_cpf"),
    window.localStorage.getItem("cpf"),
    window.localStorage.getItem("documento_empresa"),
    window.localStorage.getItem("empresa_documento"),
    window.localStorage.getItem("empresa_cnpj"),
    window.localStorage.getItem("empresa_cpf"),
    usuario?.cpf_estudante,
    usuario?.estudante_cpf,
    usuario?.empresa?.documento,
    usuario?.empresa?.cnpj,
    usuario?.empresa?.cpf,
    usuario?.empresa_vinculada?.documento,
    usuario?.empresa_vinculada?.cnpj,
    usuario?.empresa_vinculada?.cpf,
    window.localStorage.getItem("documento_usuario"),
    usuario?.documento,
    usuario?.cnpj,
    usuario?.cpf,
    payload?.cpf_estudante,
    payload?.estudante_cpf,
    payload?.documento,
    payload?.cnpj,
    payload?.cpf,
    payload?.sub,
  ];

  const encontrado = candidatos.find((valor) => String(valor || "").trim());
  return String(encontrado || "").trim();
}

export function obterUsuarioIdLogado() {
  if (typeof window === "undefined") return null;

  let usuario = {};
  try {
    usuario = JSON.parse(window.localStorage.getItem("usuario") || "{}");
  } catch {
    usuario = {};
  }

  const payload = decodificarPayloadJwt(getToken()) || {};

  const candidatos = [
    window.localStorage.getItem("usuario_id"),
    window.localStorage.getItem("id_usuario"),
    window.localStorage.getItem("user_id"),
    usuario?.id,
    usuario?.usuario_id,
    usuario?.id_usuario,
    usuario?.idUsuario,
    payload?.id,
    payload?.usuario_id,
    payload?.id_usuario,
    payload?.idUsuario,
    payload?.user_id,
    payload?.sub,
  ];

  const encontrado = candidatos.find((valor) => String(valor || "").trim());
  return encontrado ? String(encontrado).trim() : null;
}

export function obterIdentidadeSessao() {
  const payload = decodificarPayloadJwt(getToken()) || {};
  let usuario = {};
  try {
    usuario = JSON.parse(
      window.localStorage.getItem("user") ||
      window.localStorage.getItem("usuario") ||
      "{}"
    );
  } catch {
    usuario = {};
  }

  return {
    tipo: payload?.tipo || usuario?.tipo || null,
    acesso_autorizacao: Number(payload?.acesso_autorizacao ?? usuario?.acesso_autorizacao ?? 0),
    nivel: getNivelAcesso(),
    usuarioId: obterUsuarioIdLogado(),
    documento: obterDocumentoUsuarioLogado(),
  };
}

/**
 * Lista canônica de chaves relacionadas ao ESTUDANTE no localStorage.
 * Qualquer logout DEVE remover essas chaves — caso contrário, campos do
 * estudante vazam pra sessão de empresa (ex.: o `estudante_tce_id` faz o
 * TCE carregar com `?id=` errado, e o `estudante_id` aparece em forms
 * pré-preenchidos ao entrar como empresa).
 */
export const ESTUDANTE_STORAGE_KEYS = [
  "estudante_id",
  "estudante_tce_id",
  "estudante_tca_id",
];

export function limparSessaoEstudante() {
  if (typeof window === "undefined") return;
  try {
    ESTUDANTE_STORAGE_KEYS.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* localStorage indisponível */
  }
}

export function logout(navigate) {
  if (typeof window === "undefined") return;

  // Limpa todo o estado persistente para evitar vazamento entre sessões.
  try {
    window.localStorage.clear();
  } catch {}
  try {
    window.sessionStorage.clear();
  } catch {}

  if (navigate) navigate("/", { replace: true });
  else window.location.replace("/");
}

/**
 * Lê `?empresaId=` da query string atual. Retorna o id (number) ou
 * `null` se ausente / inválido. Centralizado para os 3 forms
 * (TCE / TCA / TcaEmpregador) consumirem do mesmo jeito.
 */
export function obterEmpresaIdDaUrl() {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("empresaId");
    if (!raw) return null;
    const n = Number(String(raw).trim());
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

export {
  carregarDadosEmpresaPorId,
  garantirPaginaLimpaAoCarregar,
  valorBooleanoAtivo,
  PAGINA_BLOQUEIO,
};
